import React from 'react';
import { Text, Block, TextLink } from '@snowflake/core-ui';
import { FeatureFlag, isCocoSubscriptionPromoFlow } from '@signup/shared';
import { isFeatureEnabled } from '../../utils/configurations';
import { useSignupFlowType } from '../../utils/useSignupFlowType';

// Matches the marketing bullet text color on /cortex-code (InfoList.tsx).
const FINE_PRINT_COLOR = '#5D6A85';

export function CortexFinePrint() {
  const signupFlowType = useSignupFlowType();
  const isCortexCodeV2 = isFeatureEnabled(FeatureFlag.FF_ENABLE_CORTEX_CODE_2026);
  const termsLink = (
    <TextLink
      href="https://www.snowflake.com/en/legal/terms-of-service/self-service-on-demand-terms-of-service/"
      target="_blank"
      style={{ fontSize: '14px', lineHeight: '18px', color: FINE_PRINT_COLOR }}
    >
      Snowflake Self-Service On-Demand Terms
    </TextLink>
  );

  if (isCortexCodeV2 && isCocoSubscriptionPromoFlow(signupFlowType)) {
    return (
      <Block marginTop="28px">
        <FinePrintText>
          *CoCo subscriptions are subject to usage limits across AI and compute resources; access
          may be temporarily suspended when those limits are reached. You may cancel your offer
          anytime.
        </FinePrintText>
      </Block>
    );
  }

  if (isCortexCodeV2 && isFeatureEnabled(FeatureFlag.FF_ENABLE_COCO_COMBINED_ENTITLEMENT)) {
    return (
      <Block marginTop="28px">
        <FinePrintText>
          *Includes $40 USD for Snowflake CoCo inference credits (inclusive only of CLI, Desktop,
          and Web) and $360 for all other fees including compute or storage and usage of other AI
          features for the first 30 days of trial or until you have exhausted your credits,
          whichever is earlier. You will subsequently be charged a $20 USD monthly subscription fee
          for all Snowflake CoCo inference and data platform consumption (e.g. compute or storage).
          CoCo subscriptions are subject to usage limits across AI and compute resources; access may
          be temporarily suspended when those limits are reached. You may cancel anytime.
        </FinePrintText>
      </Block>
    );
  }

  if (isCortexCodeV2) {
    return (
      <Block marginTop="28px">
        <FinePrintText>
          *Includes $40 USD for Snowflake CoCo inference credits (inclusive only of CLI, Desktop,
          and Web) and $360 for all other fees including compute or storage and usage of other AI
          features for the first 30 days of trial or until you have exhausted your credits,
          whichever is earlier. You will subsequently be charged a $20 USD monthly subscription fee
          for all Snowflake CoCo inference. Standard Snowflake data platform consumption (e.g.,
          compute or storage) and all other fees apply and are billed separately per the {termsLink}
          . You may cancel anytime.
        </FinePrintText>
      </Block>
    );
  }

  return (
    <Block marginTop="28px">
      <FinePrintText>
        *Includes $40 USD for inference credits and $360 for compute or storage for the first 30
        days of trial.
      </FinePrintText>
      <FinePrintText>
        After 30 days, you will be charged a $20 USD monthly subscription fee. Standard Snowflake
        data platform consumption (e.g., compute or storage) fees apply and are billed separately
        per the {termsLink}. You may cancel anytime.
      </FinePrintText>
    </Block>
  );
}

const FinePrintText = (props: { children: React.ReactNode }) => {
  return (
    <Text style={{ fontSize: '14px', lineHeight: '18px', color: FINE_PRINT_COLOR }}>
      {props.children}
    </Text>
  );
};
